import type { BatteryArbitrageDto } from "~/api/dto/battery-arbitrage.dto"
import useUpdateBatteryProposalSavingsInput from "~/api/hooks/useUpdateBatteryProposalSavingsInput"
import { ToggleField } from "~/components/ui-components/v2"
import { parseAxiosError } from "~/utils/parseAxiosError"

import {
  BATTERY_SAVINGS_INPUT_HELP,
  BATTERY_SAVINGS_INPUT_LABEL,
  BATTERY_SAVINGS_INPUT_UNAVAILABLE,
  savingsInputUnavailableCopy,
} from "./battery-savings-input-copy"

/**
 * Explicit Battery Savings Input: measured Green Button grid flows for
 * arbitrage only, or the proposal's household consumption. Independent of
 * Proposal Consumption Selection.
 *
 * The parent supplies the already-loaded `savingsInput`. A second
 * `useBatteryProposalArbitrage` subscriber here would refetch (staleTime is 0)
 * and skip the drawer's first lifecycle status.
 */
export const BatterySavingsInputControl = ({
  proposalId,
  savingsInput,
}: {
  proposalId: string
  savingsInput: BatteryArbitrageDto[`savingsInput`] | undefined
}): JSX.Element | null => {
  const save = useUpdateBatteryProposalSavingsInput()
  if (!savingsInput) {
    return null
  }
  const { candidate } = savingsInput
  const usingGridFlows = savingsInput.source === `green_button_grid_flows`
  const canSelectGridFlows = candidate?.eligibility === `eligible`
  const blockedReason =
    candidate?.eligibility === `ineligible`
      ? savingsInputUnavailableCopy(candidate.unavailableReason)
      : candidate
        ? null
        : BATTERY_SAVINGS_INPUT_UNAVAILABLE

  const submit = (nextChecked: boolean): void => {
    if (save.isLoading) {
      return
    }
    if (!nextChecked) {
      save.mutate({
        proposalId,
        body: { source: `proposal_consumption` },
      })
      return
    }
    if (candidate?.eligibility !== `eligible`) {
      return
    }
    save.mutate({
      proposalId,
      body: {
        source: `green_button_grid_flows`,
        importId: candidate.importId,
        expectedSourceVersion: candidate.sourceVersion,
      },
    })
  }

  return (
    <section aria-label="Battery savings input" className="mb-6">
      <ToggleField
        className="mb-2"
        label={BATTERY_SAVINGS_INPUT_LABEL}
        labelPosition="start"
        description={BATTERY_SAVINGS_INPUT_HELP}
        checked={usingGridFlows}
        disabled={save.isLoading || (!usingGridFlows && !canSelectGridFlows)}
        error={
          save.isError
            ? parseAxiosError(
                save.error,
                `Unable to change Battery savings input. Try again.`,
              )
            : undefined
        }
        onChange={submit}
      />
      {blockedReason ? (
        <p className="text-[length:var(--step--2)] text-muted">
          {blockedReason}
        </p>
      ) : usingGridFlows && candidate?.eligibility === `eligible` ? (
        <p className="text-[length:var(--step--2)] text-muted">
          {`Battery savings uses measured ${candidate.measurementBasis
            .replaceAll(`_`, ` `)
            .toLowerCase()}${
            candidate.importCoverage.hasFullYearCoverage
              ? `, a full year of intervals`
              : ``
          }.`}
        </p>
      ) : null}
    </section>
  )
}
